import { localizeUrl } from '@/lib/homepage-mappers';
import { normalizeProductSlug } from '@/lib/product-slug';

type MaybeString = string | null | undefined;

const LEGACY_PRODUCT_SLUG_ALIASES: Record<string, string> = {
  'wonder-plus': 'plus-cement',
  'wonder-plus-cement': 'plus-cement',
  'wonder-xtreme': 'xtreme-cement',
  'wonder-opc': 'opc-cement',
  'wonder-opc-cement': 'opc-cement',
  'wonder-ppc': 'ppc-cement',
  'wonder-ppc-cement': 'ppc-cement',
};

function slugifyProductTitle(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(?:^-+)|(?:-+$)/g, '');
}

export function resolveCanonicalProductSlug(raw: MaybeString): string {
  if (!raw) return '';
  const normalized = normalizeProductSlug(raw).toLowerCase();
  return LEGACY_PRODUCT_SLUG_ALIASES[normalized] ?? normalized;
}

function isProductPath(path: string): boolean {
  return /^\/products(\/|$)/.test(path);
}

function resolveHrefFromUrlOptional(urlOptional: string, locale: string): string | undefined {
  const trimmed = urlOptional.trim();
  if (/^(https?:|mailto:|tel:)/.test(trimmed)) return trimmed;

  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  if (!isProductPath(path)) {
    const localized = localizeUrl(trimmed, locale);
    if (localized) return localized;
  }

  const canonicalSlug = resolveCanonicalProductSlug(trimmed);
  if (canonicalSlug) return `/${locale}/products/${canonicalSlug}`;

  return undefined;
}

function resolveHrefFromTitle(title: string, locale: string): string | undefined {
  const titleSlug = slugifyProductTitle(title);
  const canonicalSlug = LEGACY_PRODUCT_SLUG_ALIASES[titleSlug];
  if (canonicalSlug) return `/${locale}/products/${canonicalSlug}`;
  return undefined;
}

export function resolveProductDetailHref(
  urlOptional: MaybeString,
  locale: string,
  title?: string,
): string | undefined {
  if (urlOptional?.trim() && urlOptional.trim() !== '#!') {
    const fromUrl = resolveHrefFromUrlOptional(urlOptional, locale);
    if (fromUrl) return fromUrl;
  }

  if (title?.trim()) {
    const fromTitle = resolveHrefFromTitle(title, locale);
    if (fromTitle) return fromTitle;
  }

  return undefined;
}

export function resolveProductButtonHref(
  buttonUrl: MaybeString,
  locale: string,
): string | undefined {
  return localizeUrl(buttonUrl, locale) ?? undefined;
}
